import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { authService } from '@/services/auth'
import { resetPasswordSchema, ResetPasswordFormValues } from './schemas'

function ResetPasswordForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: ''
    }
  })
  
  const onSubmit = async (data: ResetPasswordFormValues) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)
      
      const { error } = await authService.resetPassword(data.email)
      
      if (error) {
        setError(error)
        return
      }
      
      setIsSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="w-full max-w-md mx-auto p-6 space-y-6 bg-background rounded-xl shadow-lg">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text">Reset Your Password</h1>
        <p className="text-text/70 mt-2">
          Enter your email and we'll send you a link to reset your password
        </p>
      </div>
      
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}
      
      {isSuccess && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md text-green-500 text-sm">
          Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.
        </div>
      )}
      
      {!isSuccess && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-text">
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
      )}
      
      <div className="text-center text-sm text-text/70">
        Remember your password?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Back to login
        </Link>
      </div>
    </div>
  )
}

export { ResetPasswordForm }
